
import React, {
  useState,
  useEffect,
} from "react"
import {
  TextField,
  Grid,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Typography,
  Box,
  Paper,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  useTheme,
  Theme,
} from "@mui/material/styles";
import { createStyles, makeStyles } from '@mui/styles';

import { geoEquirectangular, geoPath } from "d3-geo"
import { feature } from "topojson-client"
import requestMissingContexts, { determineConfig, determineConfigLong } from "../utils/GetContext";
import { useLocalStorage } from "../utils/LocalStorageHook";
import { 
  GetStateMap,
  GetCountyMap, 
  GetCensusData,
  GetClusterLocations,
  GetClusterDetails,
} from "../utils/MapsAPI";
import { GeometryCollection } from "topojson-specification";

import { formatDate } from "../utils/Formatter";
import PageInPage from "../componentUtils/PageInPage";
import RecommendationComponent from "./request/Recommend";
import { checkForRoles } from "../utils/GetAuth";



import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  ViewCompact,
} from '@mui/icons-material';

import Color from 'color';
import CriticalFailure from "../pages/CriticalFailure"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {},
    divWidth: {},
    input: {},
    gridRoot:{},
    gridSection: {},
    gridItem:{},
    customerBox:{
      borderStyle:"",
    },
    button:{
      paddingLeft:theme.spacing(2),
      paddingRight:theme.spacing(2),
      marginTop:theme.spacing(2),
    },
    clusterPoint:{
      fill: theme.palette.mapClust.main,
    },
    clusterPointSelected:{
      fill: theme.palette.mapClust.main,
      stroke: theme.palette.mapClustSel.main,
      strokeWidth: 0.2,
    },
    rowRoot: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    chipMapTooltip: {
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(2)
    },
  })
);

const d3ProjectionMethod = geoEquirectangular()

export default function BottlerMapDisplay() {
  const theme = useTheme();
  const MIN_COLOR = Color(theme.palette.mapMin.main);
  const AVG_COLOR = Color(theme.palette.mapAvg.main);
  const MAX_COLOR = Color(theme.palette.mapMax.main);
  const SCALE_SIZE = {h:16,w:8};
  const SCALE_COUNT = 50;

  const classes = useStyles();
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const [clientId,] = useLocalStorage("SETTINGS.clientId", "100");
  const [codes, setCodes] = React.useState<any>();

  const [doneLoading, setDoneLoading] = React.useState<boolean>(false);
  const [doneRendering, setDoneRendering] = React.useState<boolean>(false);
  const [showCritFail, setShowCritFail] = React.useState<boolean>(false);
  const [drawCustomers, setDrawCustomers] = React.useState<boolean>(true);
  const [VIEWBOX, setVIEWBOX] = React.useState<string>('');
  
  const [mapTooltip, setMapTooltip] = useState<JSX.Element>()
  const [stateGeo, setStateGeo] = useState<any[]>([])
  const [countyGeo, setCountyGeo] = useState<any[]>([])
  const [censusData, setCensusData] = useState<any>({})
  const [clusterLocations, setClusterLocations] = useState<any[]>([])
  const [curCluster, setCurCluster] = useState<{clusterId:string,data:customerInfo[]}>({clusterId:'',data:[]})

  const [censusType, setCensusType] = useState<string>('total_population')
  const [curSum, setCurSum] = useState<any>({})
  const [curCounty, setCurCounty] = useState<any>({})

  function prettyPrintCensusMetric(value:number){
    let ret = '';
    if(censusType.toLowerCase().indexOf('income')>=0 || censusType.toLowerCase().indexOf('_cogs')>=0 || censusType.toLowerCase().indexOf('_revenue')>=0 || censusType.toLowerCase().indexOf('_profit')>=0){
      ret += '$'
    }
    ret += value?.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    if(censusType.toLowerCase().indexOf('percent')>=0 || censusType.toLowerCase().indexOf('_fill_rate')>=0){
      ret += '%'
    }

    return ret
  }
  function generateColorSeries(colorCount:number){
    let ret:Color[] = [];
    if(colorCount === 0) return ret;
    let offset:number = (curSum.max-curSum.min)/colorCount;
    for (let i = 0; i < colorCount; i++) {
      ret.push(calcColor(curSum.min + i * offset))
    }
    return ret;
  }

  const getColor = (STATEFP:string,COUNTYFP:string) =>
  {
    let ret = Color.rgb(80,80,80);
    let cur = censusData?.raw?.find((c:any) => c.state_fips === STATEFP && c.county_fips === COUNTYFP);
    if(cur && curSum){
      ret = calcColor(cur[censusType]);
    }
    return ret
  } 

  const calcColor = (value:number) =>
  {
    let ret = Color.rgb(110,110,110);
    if(curSum && typeof value === 'number'){
      let factor:number = 0;
      if(value === curSum.max){
        ret = MAX_COLOR
      } else if(value === curSum.avg){
        ret = AVG_COLOR
      } else if(value === curSum.max){
        ret = MAX_COLOR
      } else if(value < curSum.avg){
        factor = (value - curSum.min) / (curSum.avg - curSum.min)
        if(factor > 1) factor = 1
        ret = MIN_COLOR.mix(AVG_COLOR,factor)
      } else if(value > curSum.avg){
        factor = (value - curSum.avg) / (curSum.max - curSum.avg)
        if(factor > 1) factor = 1
        ret = AVG_COLOR.mix(MAX_COLOR,factor)
      }
    }
    return ret
  } 
  const handleClusterClick = (clusterId:string) => {
    setCurCluster({clusterId,data:[]});
    GetClusterDetails(clientId,salesOrg,clusterId)
      .then((rsp) => { 
        let cluster = {
          clusterId,
          data:rsp.data,
        }
        setCurCluster(cluster);
      })

  }
  const handleCountyHover = (omoEvent:React.MouseEvent<SVGPathElement, MouseEvent>,STATEFP:string,COUNTYFP:string) =>
  {
    let cur = censusData?.raw?.find((c:any) => c.state_fips === STATEFP && c.county_fips === COUNTYFP);
    setCurCounty(cur||{});
    // setMapTooltip(
    //   <Card variant="outlined" style={
    //       {
    //         position:'absolute',
    //         left:`${omoEvent.pageX+10}px`,
    //         top:`${omoEvent.pageY+10}px`,
    //         zIndex:999999,
    //       }
    //     }>
    //     <CardContent>
    //       <div>{`${cur.county_name||''}, ${cur.state_name||''}`}</div>
    //       <div>
    //         {determineConfigLong(codes, "census_types", censusType) || censusType}{": "} 
    //         {
    //           (typeof cur[censusType] === 'number')?
    //           (censusType.toLowerCase().indexOf('income')>=0?'$':'')+
    //           cur[censusType].toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')+
    //           (censusType.toLowerCase().indexOf('percent')>=0?'%':'')
    //           :''
    //         }
    //       </div>
    //     </CardContent>
    //   </Card>
    // );
  } 
  
  const handleClusterHover = (omoEvent:React.MouseEvent<SVGCircleElement, MouseEvent>,ClusterCount:number) =>
  {
    setMapTooltip(
      <Chip 
        style={
          {
            position:'absolute',
            left:`${omoEvent.pageX}px`,
            top:`${omoEvent.pageY}px`,
            zIndex:999999,
          }
        }
        label={`${ClusterCount} Customers`}
        className={classes.chipMapTooltip}
      />
    );
  } 
  useEffect(() => {
    if(doneRendering) return;
    if(!doneLoading)
    {
      let state:any;
      let county:any;
      let census:any;
      let cluster:any;
      Promise.all([
        (GetStateMap(clientId,salesOrg).then((rsp) => { state = rsp.data})).catch((err) => {console.error('Error while trying to get states map from the server'); setShowCritFail(true);}),
        (GetCountyMap(clientId,salesOrg).then((rsp) => { county = rsp.data})).catch((err) => {console.error('Error while trying to get county map from the server'); setShowCritFail(true); }),
        (GetCensusData(clientId,salesOrg).then((rsp) => { census = rsp.data})).catch((err) => {console.warn('Error while trying to get census data from the server');}),
        (GetClusterLocations(clientId,salesOrg).then((rsp) => { cluster = rsp.data})).catch((err) => {console.warn('Error while trying to get vending machine cluster locations from the server');}),
        (requestMissingContexts(["SALESOFFICES","census_types"]).then((v:any) => {setCodes(v);})).catch((err) => {console.warn('Error while trying to get test descriptions from the server');}),
      ]).then( ()=>{
        if(state) {
          setStateGeo(feature(state, state.objects[`State_${salesOrg}`] as GeometryCollection).features);

          // dynamic viewbox based on the state geo structure 
          let [minX, minY, maxX, maxY] = [180,90,-180,-90]
          // scan all arcs for min/max geography by lat/long
          state.arcs.forEach((arc:number[][])=>{
            arc.forEach((point:number[])=>{  
              minX = (minX>point[0])?point[0]:minX;
              minY = (minY>point[1])?point[1]:minY;
              maxX = (maxX<point[0])?point[0]:maxX;
              maxY = (maxY<point[1])?point[1]:maxY;
            });
          });

          // convert min x/y from geo loacation to projected point
          [minX,minY] = d3ProjectionMethod([minX, minY])||[0,0];
          
          // convert max x/y from geo loacation to projected point 
          [maxX,maxY] = d3ProjectionMethod([maxX, maxY])||[0,0];
          
          //build viewbox params, looks funny but hopefully works with positive and negitive geopoints 
          let vb={
            x:Math.floor(Math.min(minX,maxX))-1,
            y:Math.floor(Math.min(minY,maxY))-1,
            width:Math.ceil(Math.abs(minX-maxX))+2,
            height:Math.ceil(Math.abs(minY-maxY))+2,
          }
          setVIEWBOX(`${vb.x} ${vb.y} ${vb.width} ${vb.height}`);
        }

        if(county){
          setCountyGeo(feature(county, county.objects[`County_${salesOrg}`] as GeometryCollection).features);
        }

        if(census){
          setCensusData(census);
          let sum = census.sum.find((s:any) => s.metric === censusType);
          if(sum){
            setCurSum(sum)
          }
        }
        
        if(cluster){
          setClusterLocations(cluster);
        }

        setDoneLoading(true);
        setDoneRendering(true);
      })
    } else {
      setDoneRendering(true);
    }
      
  }, [censusType, clientId, doneLoading, doneRendering, salesOrg])

  if(showCritFail){
    return(
      <CriticalFailure title='Maps - ERROR'>
        <div>Unable to get required data from the server. Map cannot be displayed.</div>
      </CriticalFailure>
    )
  }

  return (
    <div>
      {mapTooltip}
      {(doneLoading)?(
        <Grid container spacing={2} className={classes.gridRoot}>
          <Grid item  xs={12} sm={12} md={6} lg={8} xl={8}  className={classes.gridItem}>
            <Grid container spacing={2} className={classes.gridRoot}>
              <Grid item  xs={6} sm={6} md={6} lg={6} className={classes.gridItem}>
                <div style={{width:SCALE_SIZE.w * SCALE_COUNT}} >
                  <Typography variant="h6" component="div">
                    {determineConfigLong(codes, "census_types", censusType) || censusType}
                  </Typography>
                  <svg width={SCALE_SIZE.w * SCALE_COUNT} height={SCALE_SIZE.h}>
                    {generateColorSeries(SCALE_COUNT).map((c,i)=>(
                      <rect 
                          key={ `color-scale-${ i }` }
                          x={i * SCALE_SIZE.w} y="0" 
                          width={SCALE_SIZE.w} height={SCALE_SIZE.h} 
                          fill={c.toString()} />
                    ))}
                  </svg>
                  <div style={{float:"left"}}>
                    {prettyPrintCensusMetric(curSum.min)}
                  </div>
                  <div style={{float:"right"}}>
                    {prettyPrintCensusMetric(curSum.max)}
                  </div>
                </div>
              </Grid>
              <Grid item  xs={6} sm={6} md={6} lg={6} className={classes.gridItem}>
                  <Tooltip title="Show/hide the vending machine locations">
                    <FormControlLabel 
                      label="Show VM"
                      control={
                        <Switch 
                          checked={drawCustomers}
                          onClick={() => {setDrawCustomers(!drawCustomers);}}
                      />
                      }
                    />
                  </Tooltip>
              </Grid>
            </Grid>
            <svg viewBox={VIEWBOX}> 
              <g className="states" fill={`rgba(250,250,250,1)`}>
                {
                  stateGeo.map((d,i) => (
                    <path
                      key={ `path-state-fill-${ i }` }
                      d={ geoPath().projection(d3ProjectionMethod)(d) || '' }
                    />
                  ))
                }
              </g>
              <g className="countys" stroke="#FFFFFF" strokeWidth={ 0.01 }>
              {
                (countyGeo)?(
                  countyGeo.map((d,i) => (
                    <path
                      key={ `county-${d.properties.STATEFP}-${d.properties.COUNTYFP}-${i}`}
                      d={ geoPath().projection(d3ProjectionMethod)(d) || '' }
                      className="county"
                      fill={getColor(d.properties.STATEFP,d.properties.COUNTYFP).toString()}
                      onMouseOver={ (omo) => handleCountyHover(omo,d.properties.STATEFP,d.properties.COUNTYFP) }
                      onMouseOut={()=>setMapTooltip(<></>)}
                    />
                  ))
                ):(<g/>)
              }
              </g>
              <g className="states-border" fill="none" stroke="#000000" strokeWidth={ 0.05 }>
                {
                  stateGeo.map((d,i) => (
                    <path
                      key={ `path-state-border-${ i }` }
                      d={ geoPath().projection(d3ProjectionMethod)(d) || '' }
                    />
                  ))
                }
              </g>
              <g className="clusters">
                  {(drawCustomers && clusterLocations)?(
                    clusterLocations.map((cl, i) => (
                      <circle
                        key={ `marker-${i}` }
                        cx={ d3ProjectionMethod([cl.longitude, cl.latitude])?.[0]}
                        cy={ d3ProjectionMethod([cl.longitude, cl.latitude])?.[1]}
                        r={ Math.log(2*cl.customer_count + 5)/20 }
                        className={(curCluster.clusterId === cl.cluster_id)?classes.clusterPointSelected:classes.clusterPoint}
                        onClick={ () => handleClusterClick(cl.cluster_id) }
                        onMouseOver={ (omo) => handleClusterHover(omo,cl.customer_count)}
                        onMouseOut={()=>setMapTooltip(<></>)}
                      />
                    ))
                  ):(<g/>)
                  }
                </g>
          </svg>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={4} xl={4} className={classes.gridItem}>
          <Grid container spacing={2} className={classes.gridRoot}>
            <Grid item xs={6} xl={6} className={classes.gridItem}>
              <InputLabel>{`${curCounty.county_name||''}, ${curCounty.state_name||''}`}</InputLabel>
              <TextField placeholder='Location' type='text' fullWidth={true}
                  value={
                    (typeof curCounty[censusType] === 'number')?
                    prettyPrintCensusMetric(curCounty[censusType])
                    :''
                  } />
            </Grid>
            <Grid item xs={6} xl={6} className={classes.gridItem}>
              <InputLabel>Census Data</InputLabel>
              <Select label="Census Data" required placeholder='Census Data' value={censusType||''}
                inputProps={{name: 'Census Data',}}
                fullWidth={true}
                onChange={(e) => {
                  let newType = (e.target.value && typeof e.target.value == 'string') ? e.target.value : ""
                  let sum = censusData.sum.find((s:any) => s.metric === newType);
                  if(sum){
                    setCurSum(sum)
                    setCensusType(newType);
                    setDoneRendering(false);
                  }
                }}
                >
                  {
                    censusData.sum?.sort((a:any,b:any)=> ((determineConfig(codes, "census_types", a.metric) || a.metric) > (determineConfig(codes, "census_types", b.metric) || b.metric)) ? 1 : -1)
                        .map((s: any, i: number) => (<MenuItem key={s.metric} value={s.metric}>{determineConfig(codes, "census_types", s.metric) || s.metric}</MenuItem>))
                  }
              </Select>
            </Grid>
          </Grid>
          <br />
        <ClusterCustomerTable clusterId={curCluster.clusterId} customers={curCluster.data} />
      </Grid>
    </Grid>
    ):(
    <div className={classes.centerDiv}>
      <CircularProgress color="primary" />
    </div>
    )}
  <div className={classes.centerDiv}>
    <Typography variant="h6" component="div">
      This product uses the Census Bureau Data API but is not endorsed or certified by the Census Bureau.
    </Typography>

  </div>
  </div>  
  )
}

interface customerInfo{
	sales_office:string;
	customer_num:string;
	name1:string;
	name2:string;
	name3:string;
	street:string;
	city:string;
	state:string;
	zipcode:string;
	district:string;
	master_route:string;
	graphic:string;
	manufacturer:string;
	model:string;
	settlement_method:number;
	building:string;
	floor:string;
	room:string;
	status:string;
	last_fill_units:number;
	last_fill_rate:number;
	effective_fill_rate:number;
	daily_cogs:number;
	daily_revenue:number;
	material_consumption:number;
  last_visit_dt:Date;
  next_visit_dt_plan:Date;
  next_visit_dt_act:Date;
}
function ClusterCustomerTable(props:{clusterId:string,customers:customerInfo[]}){
  const { clusterId, customers } = props;
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const header =  document.querySelector('ResponsiveDrawer')
  const headerAdj = header?header.clientHeight:200;
  const [TABLE_BODY_HEIGHT, setTABLE_BODY_HEIGHT] = React.useState<string>(`${window.innerHeight - headerAdj }px`);
  window.addEventListener('resize', ()=>{
    setTABLE_BODY_HEIGHT(`${window.innerHeight - headerAdj }px`);
  }) 

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Paper className={classes.root}>
      <TableContainer style={{maxHeight: TABLE_BODY_HEIGHT,}}>
        <Table stickyHeader aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Route</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Unit/Day</TableCell>
              <TableCell align="right">LFR</TableCell>
              <TableCell align="right">PFR@OOS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers?.sort((a,b) => {return (100*b.material_consumption + b.last_fill_rate) - (100*a.material_consumption + a.last_fill_rate)}).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <ClusterCustomerDetail customer={row} key={`ccd-${row.customer_num}`}/>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={customers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    {(clusterId && customers.length === 0)?(
      <div className={classes.centerDiv}>
        <CircularProgress color="primary" />
      </div>
    ):(<div></div>)}
    </Paper>
  );
}

function ClusterCustomerDetail(props:{customer:customerInfo}){
  const { customer } = props;
  const [detailIsOpen, setDetailIsOpen] = React.useState(false);
  const [recoIsOpen, setRecoIsOpen] = React.useState(false);
  const classes = useStyles();
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const [clientId,] = useLocalStorage("SETTINGS.clientId", "100");
  return (
    <React.Fragment>
      <TableRow className={classes.rowRoot}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setDetailIsOpen(!detailIsOpen)}>
            {detailIsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{customer.sales_office} / {customer.master_route}</TableCell>
        <TableCell>{customer.customer_num}</TableCell>
        <TableCell>{customer.model}</TableCell>
        <TableCell>{customer.status}</TableCell>
        <TableCell align="right">{customer.material_consumption?(customer.last_fill_rate).toPrecision(2):''}</TableCell>
        <TableCell align="right">{customer.last_fill_rate?`${(100*customer.last_fill_rate).toPrecision(2)}%`:''}</TableCell>
        <TableCell align="right">{customer.effective_fill_rate?`${customer.effective_fill_rate.toPrecision(2)}%`:''}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={detailIsOpen} timeout="auto" unmountOnExit >
            <Box margin={4} width="100%" className={classes.customerBox}>
              <Typography variant="h6" component="div">
                {customer.name1}{customer.name2?` ${customer.name2}`:''}
              </Typography>
              <Typography variant="subtitle2" component="div" align="right">
                <div style={{float:"left"}}>
                  {customer.name3?` [${customer.name3}]`:''}
                </div>
                {customer.street}<br />
                {customer.city}{" "}{customer.state}{", "}{customer.zipcode}
              </Typography>
              <br />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Settlement</TableCell>
                    <TableCell align="right">Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{customer.manufacturer}</TableCell>
                    <TableCell>{customer.model}</TableCell>
                    <TableCell align="right">{customer.status}</TableCell>
                    <TableCell align="right">{customer.settlement_method}</TableCell>
                    <TableCell align="right">{customer.building}{" "}{customer.floor}{" "}{customer.room}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <br />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="right">LFR</TableCell>
                    <TableCell align="right">PFR@OOS</TableCell>
                    <TableCell align="right">LFU</TableCell>
                    <TableCell align="right">Unit/Day</TableCell>
                    <TableCell align="right">COGS/Day</TableCell>
                    <TableCell align="right">Rev/Day</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align="right">{customer.last_fill_rate?`${(100*customer.last_fill_rate).toPrecision(2)}%`:''}</TableCell>
                    <TableCell align="right">{customer.effective_fill_rate?`${customer.effective_fill_rate.toPrecision(2)}%`:''}</TableCell>
                    <TableCell align="right">{customer.last_fill_units}</TableCell>
                    <TableCell align="right">{customer.material_consumption?(customer.material_consumption).toPrecision(2):''}</TableCell>
                    <TableCell align="right">${(customer.daily_cogs||0).toPrecision(2)}</TableCell>
	                  <TableCell align="right">${(customer.daily_revenue||0).toPrecision(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <br />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="right">Last Visit</TableCell>
                    <TableCell align="right">Next Proposed</TableCell>
                    <TableCell align="right">Next Planned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                  <TableCell align="right">{formatDate(customer.last_visit_dt)}</TableCell>
                  <TableCell align="right">{formatDate(customer.next_visit_dt_plan)}</TableCell>
                    <TableCell align="right">{formatDate(customer.next_visit_dt_act)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {(checkForRoles(clientId, salesOrg, ["vp_create_ticket","vp_approve_ticket"]))?(
                <Button
                  className={classes.button}
                  color="primary"
                  variant="contained"
                  key={`EquipmentUnitIndiv-reconfigbutton`}
                  onClick={() => setRecoIsOpen(true)}
                  disabled={customer.settlement_method !== 5}
                >
                  <ViewCompact style={{ marginRight: "5px" }} />
                  Optimize Space to Sales
                </Button>
              ):(<div/>)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      
      <PageInPage
        onClose={() => {
          setRecoIsOpen(false);
        }}
        title={`Reconfiguration for ${customer.sales_office}: ${customer.name1}`}
        isOpen={recoIsOpen}
        content={
          <RecommendationComponent
            fullpage={false}
            customer={customer.customer_num}
          />
        }
      />
    </React.Fragment>
  )
}